import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Set `window.__SHOP_HIGH_CART_DEBUG__ = false` to silence these logs. */
function cartDebug(...args) {
  if (typeof window !== 'undefined' && window.__SHOP_HIGH_CART_DEBUG__ !== false) {
    console.log('[shop-high cart-items]', ...args);
  }
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Removes a line item by variant id using /cart/update (safer than /cart/change by line
   * in stores using bundles/parent-child lines where removing one line can clear a group).
   * @param {string|number} variantId
   * @param {Event} event
   */
  onVariantRemove(variantId, event) {
    event?.preventDefault?.();

    const id = String(variantId || '').trim();
    if (!id) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`remove:user-action`);
    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    /** @type {Record<string, number>} */
    const updates = {};
    updates[id] = 0;

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartDebug(`POST /cart/update payload (variant=${id})`, {
      sectionId: this.sectionId,
      updates,
      sections: Array.from(sectionsToUpdate),
      body,
    });

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((r) => r.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          cartDebug('/cart/update response ERROR', { sectionId: this.sectionId, errors: parsedResponseText.errors });
          return;
        }

        const items = parsedResponseText.items;
        cartDebug(
          `/cart/update response OK (section=${this.sectionId}, item_count=${parsedResponseText.item_count}, items_len=${
            Array.isArray(items) ? items.length : 'null'
          })`,
          {
            sectionId: this.sectionId,
            itemCount: parsedResponseText.item_count,
            itemsLength: Array.isArray(items) ? items.length : null,
            variantIds: Array.isArray(items) ? items.map((i) => i.variant_id) : null,
          }
        );

        // Update data-cart-quantity for all matching variants in the cart
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: parsedResponseText.item_count,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (parsedResponseText.sections?.[this.sectionId]) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        }
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    cartDebug('quantity-selector update', {
      sectionId: this.sectionId,
      quantity,
      cartLine: line,
      targetTag: event.target instanceof Element ? event.target.tagName : typeof event.target,
    });

    // Cart items require a line number
    if (!line) {
      cartDebug('quantity-selector update SKIPPED: missing cartLine (data-cart-line on input?)');
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows[line - 1];
    cartDebug('onLineItemRemove', {
      sectionId: this.sectionId,
      line,
      rowKey: row instanceof HTMLElement ? row.dataset.key : null,
      rowParentKey: row instanceof HTMLElement ? row.dataset.parentKey : null,
      rowCount: this.refs.cartItemRows?.length,
      rowKeysByLine: Array.isArray(this.refs.cartItemRows)
        ? this.refs.cartItemRows.map((r, idx) => ({
            line: idx + 1,
            key: r instanceof HTMLElement ? r.dataset.key : null,
            parentKey: r instanceof HTMLElement ? r.dataset.parentKey : null,
          }))
        : null,
    });

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'remove',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartDebug(
      `POST /cart/change payload (section=${this.sectionId}, action=${config.action}, line=${line}, qty=${quantity})`,
      {
      sectionId: this.sectionId,
      action: config.action,
      line,
      quantity,
      sections: Array.from(sectionsToUpdate),
      body,
      }
    );

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          cartDebug('/cart/change response ERROR', {
            sectionId: this.sectionId,
            line,
            errors: parsedResponseText.errors,
          });
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const items = parsedResponseText.items;
        cartDebug(
          `/cart/change response OK (section=${this.sectionId}, line=${line}, item_count=${parsedResponseText.item_count}, items_len=${
            Array.isArray(items) ? items.length : 'null'
          })`,
          {
          sectionId: this.sectionId,
          lineRequested: line,
          itemCount: parsedResponseText.item_count,
          itemsLength: Array.isArray(items) ? items.length : null,
          variantIds: Array.isArray(items) ? items.map((i) => i.variant_id) : null,
          }
        );

        // Confirm server state (is the cart actually empty?)
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cartJson) => {
            cartDebug('GET /cart.js after change', {
              item_count: cartJson?.item_count,
              items_len: Array.isArray(cartJson?.items) ? cartJson.items.length : null,
              variantIds: Array.isArray(cartJson?.items) ? cartJson.items.map((i) => i.variant_id) : null,
              keys: Array.isArray(cartJson?.items) ? cartJson.items.map((i) => i.key) : null,
            });
          })
          .catch(() => {});

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      cartDebug('#handleCartUpdate → renderSection (discount)', { sectionId: this.sectionId });
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      cartDebug('#handleCartUpdate → morphSection (has sections html)', {
        sectionId: this.sectionId,
        eventType: event.constructor?.name,
      });
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    // `layout/theme.liquid` dispatches plain `CustomEvent('cart:update')` with `itemCount` only,
    // then `refreshCartUI()` replaces cart-related sections. Calling `renderSection` here starts
    // an async section fetch that often finishes *after* the drawer opens and morphs the DOM back
    // to server HTML with a closed dialog (drawer flashes open then shut).
    if (!(event instanceof CartAddEvent) && !(event instanceof CartUpdateEvent)) {
      return;
    }

    cartDebug('#handleCartUpdate → renderSection (CartAdd/CartUpdate, no embedded section)', {
      sectionId: this.sectionId,
      eventType: event.constructor?.name,
    });
    sectionRenderer.renderSection(this.sectionId, { cache: false });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
